import * as THREE from 'three';
import { FamilySchema, GeometryInstruction } from './FamilySchema';
import { SolidKernel } from '../SolidKernel';

export class ParametricGeometryGenerator {

    /**
     * Generate Mesh
     * Computes the 3D geometry for a specific set of parameter values.
     * @param schema The family schema defining geometry.
     * @param parameterValues Resolved parameters.
     * @param instanceResolver Callback to generate nested family instances.
     */
    static generate(
        schema: FamilySchema,
        parameterValues: Record<string, any>,
        instanceResolver?: (familyId: string, typeId: string, params: Record<string, any>) => THREE.Group | null
    ): THREE.Group {
        const root = new THREE.Group();
        const meshMap = new Map<string, THREE.Mesh>();

        // 1. Process "Leaf" Geometry (Solids)
        const solids = schema.geometry.filter(g =>
            ['Extrude', 'Sweep', 'Revolve', 'Blend', 'Instance'].includes(g.operation)
        );

        solids.forEach(instruction => {
            let mesh: THREE.Object3D | null = null;
            if (instruction.operation === 'Instance') {
                mesh = this.createInstance(instruction, parameterValues, instanceResolver);
            } else {
                mesh = this.createSolid(instruction, parameterValues);
            }
            if (mesh) {
                mesh.name = instruction.id;
                if (mesh instanceof THREE.Mesh) {
                    meshMap.set(instruction.id, mesh);
                } else {
                    root.add(mesh);
                }
            }
        });

        // 2. Process Boolean Operations
        const booleans = schema.geometry.filter(g =>
            ['Union', 'Subtract', 'Intersect'].includes(g.operation)
        );

        const consumedIds = new Set<string>();

        booleans.forEach(op => {
            if (op.operands && op.operands.length >= 2) {
                const meshA = meshMap.get(op.operands[0]);
                const meshB = meshMap.get(op.operands[1]);

                if (meshA && meshB) {
                    let result: THREE.Mesh | null = null;
                    if (op.operation === 'Union') result = SolidKernel.union(meshA, meshB);
                    else if (op.operation === 'Subtract') result = SolidKernel.subtract(meshA, meshB);
                    else if (op.operation === 'Intersect') result = SolidKernel.intersect(meshA, meshB);

                    if (result) {
                        result.name = op.id;
                        meshMap.set(op.id, result);
                        consumedIds.add(op.operands[0]);
                        consumedIds.add(op.operands[1]);
                    }
                }
            }
        });

        // 3. Assemble Final Output
        meshMap.forEach((mesh, id) => {
            if (!consumedIds.has(id)) {
                root.add(mesh);
            }
        });

        return root;
    }

    private static createSolid(instruction: GeometryInstruction, params: Record<string, any>): THREE.Mesh | null {
        const resolve = (val: string | number | undefined): number => {
            if (typeof val === 'number') return val;
            if (!val) return 0;
            const trimmed = val.trim();
            // Plain numeric literal ("0", "-10", "915") short-circuits.
            if (/^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);
            // BIM-style formula syntax: substitute every $Param reference
            // with its value, then evaluate the remaining arithmetic
            // expression ("- $FrameWidth", "$Height + $FrameWidth", "$Width").
            // Note: expressions that merely START with a number sign but
            // contain '$' (e.g. "-$FrameWidth") must go through here, not
            // parseFloat.
            const expr = trimmed.replace(/\$([a-zA-Z0-9_]+)/g, (_match, p1) => {
                const v = params[p1];
                return v !== undefined ? String(v) : '0';
            });
            try {
                const result = new Function('"use strict"; return (' + expr + ')')();
                return typeof result === 'number' && !Number.isNaN(result) ? result : 0;
            } catch {
                const f = parseFloat(trimmed);
                return isNaN(f) ? 0 : f;
            }
        };

        if (instruction.operation === 'Extrude') {
            if (!instruction.profile) return null;

            const shape = new THREE.Shape();
            const points = instruction.profile.map(p => ({
                x: typeof p.x === 'string' ? resolve(p.x as string) : p.x,
                y: typeof p.y === 'string' ? resolve(p.y as string) : p.y
            }));

            if (points.length < 3) return null;

            shape.moveTo(points[0].x, points[0].y);
            for (let i = 1; i < points.length; i++) {
                shape.lineTo(points[i].x, points[i].y);
            }
            shape.closePath();

            const height = resolve(instruction.height);

            const geometry = new THREE.ExtrudeGeometry(shape, {
                depth: height,
                bevelEnabled: false
            });

            if (instruction.position) {
                const x = resolve(instruction.position.x);
                const y = resolve(instruction.position.y);
                const z = resolve(instruction.position.z);
                geometry.translate(x, y, z);
            }

            const material = new THREE.MeshStandardMaterial({
                color: instruction.name === 'Panel' ? 0x8B4513 : 0x333333,
                name: instruction.id
            });

            return new THREE.Mesh(geometry, material);
        }
        return null;
    }

    private static createInstance(
        instruction: GeometryInstruction,
        params: Record<string, any>,
        resolver?: (familyId: string, typeId: string, params: Record<string, any>) => THREE.Group | null
    ): THREE.Group | null {
        if (!instruction.familyId || !resolver) return null;

        const nestedParams: Record<string, any> = {};
        if (instruction.instanceParams) {
            const resolve = (val: string | number | undefined): any => {
                if (typeof val === 'number') return val;
                if (!val) return 0;
                if (val.startsWith('$')) {
                    const expr = val.replace(/\$([a-zA-Z0-9_]+)/g, (match, p1) => {
                        const v = params[p1];
                        return v !== undefined ? v : 0;
                    });
                    try { return new Function('return ' + expr)(); } catch { return 0; }
                }
                const f = parseFloat(val);
                return isNaN(f) ? val : f;
            };

            for (const [key, val] of Object.entries(instruction.instanceParams)) {
                nestedParams[key] = resolve(val);
            }
        }

        try {
            const group = resolver(instruction.familyId, instruction.typeId || 'Standard', nestedParams);
            if (group && instruction.position) {
                const x = parseFloat(instruction.position.x) || 0;
                const y = parseFloat(instruction.position.y) || 0;
                const z = parseFloat(instruction.position.z) || 0;
                group.position.set(x, y, z);
            }
            return group;
        } catch {
            return null;
        }
    }
}
